import { giphyRed } from '@giphy/js-brand'
import { IGif } from '@giphy/js-types'
import { GifOverlayProps } from '@giphy/react-components'
import React, { useContext, useRef } from 'react'
import { getIsFavorite } from 'shared/api/favorites'
import Lock from 'shared/components/icons/lock'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import { getShareUrl } from 'shared/util/gif-extract'
import { removeAllQueryParams } from 'shared/util/url-utils'
import styled, { css } from 'styled-components'
import { Clipboard } from 'ts-clipboard'
import GAEventContext from './context/ga-events'
import MessageContext from './context/message'
import ModalContext from './context/modal'
import UserContext from './context/user'
import Favorite from './favorite/favorite'

export const LockOverlay = ({ gif, isHovered }: GifOverlayProps) =>
    !isHovered && gif?.is_hidden ? (
        <LockContainer>
            <Lock />
        </LockContainer>
    ) : null

export const FavoriteOverlay = ({ gif, isHovered }: GifOverlayProps) => {
    const hasFetched = useRef(false)
    const { onGifFavorite } = useContext(GAEventContext)
    const { favorites, addFavorite, removeFavorite, isLoggedIn } = useContext(UserContext)
    useAsyncEffect(async () => {
        if (isLoggedIn && isHovered && !hasFetched.current) {
            // until we have an endpoint where we can get
            // all the user's favorites, we have to fetch them individually
            hasFetched.current = true
            const isFav = await getIsFavorite(gif.id)
            isFav ? addFavorite(gif.id) : removeFavorite(gif.id)
        }
    }, [isHovered])
    const isFavorite = favorites.indexOf(gif.id) !== -1
    return (
        <Favorite
            id={gif.id}
            url={gif.url}
            isFavorite={isFavorite}
            onClick={(gif: IGif) => {
                if (isFavorite) {
                    removeFavorite(gif.id)
                } else {
                    onGifFavorite(gif)
                    addFavorite(gif.id)
                }
            }}
        />
    )
}

const GifMenuOverlay = ({ gif, isHovered }) => {
    return (
        <>
            <LockOverlay gif={gif} isHovered={isHovered} />
            <Overlay isHovered={isHovered}>
                <EditButton gif={gif} />
                <ClipboardButton gif={gif} />
                <FavoriteOverlay gif={gif} isHovered={isHovered} />
            </Overlay>
        </>
    )
}

const HoverButton = styled.div`
    &:hover {
        transform: scale(1.2);
        transition: transform 200ms ease-out;
    }
`

const Edit = styled(HoverButton)`
    margin: 10px 18px 0 0;
`
const EditButton = ({ gif }) => {
    const { user } = useContext(UserContext)
    const { openGifEditPanel } = useContext(ModalContext)
    return user.isStaff ? (
        <Edit
            className="ss-write"
            onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                openGifEditPanel(gif)
            }}
        />
    ) : null
}

const Link = styled(HoverButton)`
    margin: 10px 10px 0 0;
`
const ClipboardButton = ({ gif }) => {
    const { sendMessage } = useContext(MessageContext)
    const { onLinkCopy } = useContext(GAEventContext)
    const {
        images: {
            original: { url },
        },
    } = gif
    return (
        <Link
            className="ss-link"
            onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                try {
                    Clipboard.copy(removeAllQueryParams(getShareUrl(url)))
                    sendMessage('Link copied to clipboard!', 'success')
                    onLinkCopy(gif)
                } catch (error) {
                    sendMessage('Error copying to clipboard :(', 'error')
                }
            }}
        />
    )
}

const Overlay = styled.div<{ isHovered: boolean }>`
    display: flex;
    position: absolute;
    top: 0;
    right: 0;
    ${props =>
        !props.isHovered &&
        css`
            display: none;
        `}
`

const LockContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    fill: ${giphyRed};
    background: rgba(0, 0, 0, 0.5);
    pointer-events: none;
`

export default GifMenuOverlay
